import { useEffect, useState } from "react";
import { Sparkles, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { memberApi } from "../api";
import { useAuth } from "../AuthContext";

const MINDBODY_BOOK = "https://clients.mindbodyonline.com/classic/mainclass?fl=true&tabID=7";
const MINDBODY_SHOP = "https://clients.mindbodyonline.com/asp/main_shop.asp?pMode=0&tabID=3";

export default function Dashboard() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    memberApi
      .get("/api/news")
      .then((r) => setPosts(r.data.posts || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="member-dashboard">
      {/* Quick actions */}
      <section>
        <h2 className="text-xs uppercase tracking-[0.2em] text-[#E8DCC4]/60 mb-3 font-medium">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-3">
          <a
            href={MINDBODY_BOOK}
            target="_blank"
            rel="noreferrer"
            data-testid="dashboard-book-cta"
            className="bg-gradient-to-br from-[#C97654] to-[#FF9472] rounded-2xl p-4 text-white shadow-lg active:scale-95 transition-transform flex flex-col items-start gap-1"
          >
            <Sparkles className="w-5 h-5 opacity-90" />
            <span className="font-bold text-sm">Book a Class</span>
            <span className="text-xs opacity-80">View schedule →</span>
          </a>
          <a
            href={MINDBODY_SHOP}
            target="_blank"
            rel="noreferrer"
            data-testid="dashboard-shop-cta"
            className="bg-gradient-to-br from-[#8B2635] to-[#a03042] rounded-2xl p-4 text-white shadow-lg active:scale-95 transition-transform flex flex-col items-start gap-1"
          >
            <ExternalLink className="w-5 h-5 opacity-90" />
            <span className="font-bold text-sm">Buy Memberships</span>
            <span className="text-xs opacity-80">Packs &amp; classes →</span>
          </a>
        </div>
      </section>

      {/* News feed */}
      <section>
        <h2 className="text-xs uppercase tracking-[0.2em] text-[#E8DCC4]/60 mb-3 font-medium">Studio News</h2>
        {loading ? (
          <div className="bg-[#1a1a1a] rounded-2xl p-6 text-center text-[#E8DCC4]/50 text-sm">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-6 text-center" data-testid="news-empty">
            <p className="text-[#E8DCC4]/70 text-sm mb-3">No news yet — check back soon for updates from the studio!</p>
            <Link to="/app/referrals" className="text-[#C97654] text-sm font-medium hover:text-[#FF9472]">
              In the meantime, refer a friend ✨
            </Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="news-feed">
            {posts.map((post) => (
              <article key={post.id} className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl overflow-hidden">
                {post.image_url && (
                  <img src={post.image_url} alt="" className="w-full h-44 object-cover" loading="lazy" />
                )}
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#C97654] mb-1.5">
                    {post.author_name || "Passion Pilates"} · {new Date(post.created_at).toLocaleDateString()}
                  </p>
                  <h3 className="text-lg font-medium text-[#E8DCC4] mb-2">{post.title}</h3>
                  <p className="text-sm text-[#E8DCC4]/80 leading-relaxed whitespace-pre-line">{post.body}</p>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Member welcome card */}
      {user?.referral_code && (
        <section className="bg-gradient-to-br from-[#1a1a1a] to-[#2a1115] border border-[#8B2635]/40 rounded-2xl p-5 text-center">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#C97654] mb-2 font-bold">Your Referral Code</p>
          <p className="text-2xl font-bold text-[#E8DCC4] tracking-widest">{user.referral_code}</p>
          <Link to="/app/referrals" className="inline-block mt-3 text-xs text-[#C97654] hover:text-[#FF9472] underline underline-offset-4">
            Share &amp; earn perks →
          </Link>
        </section>
      )}
    </div>
  );
}
